import React, { useEffect, useState } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { CalculatorIcon, SlidersHorizontalIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
type LifecyclePhase = 'launch' | 'growth' | 'established';
interface PhaseConfig {
  label: string;
  portfolioLabel: string;
  sliderLabel: string;
  share: number;
  min: number;
  max: number;
  rationale: string;
}
const PHASES: Record<LifecyclePhase, PhaseConfig> = {
  launch: {
    label: 'Launch',
    portfolioLabel: 'Launch Brands',
    sliderLabel: '% of revenue from Launch Brands',
    share: 12,
    min: 1,
    max: 30,
    rationale: 'For Launch Brands, the model illustrates the revenue value of improving early adoption across the selected share of group revenue.'
  },
  growth: {
    label: 'Growth',
    portfolioLabel: 'Growth Brands',
    sliderLabel: '% of revenue from Growth Brands',
    share: 44,
    min: 5,
    max: 70,
    rationale: 'For Growth Brands, the model illustrates the revenue value of increasing adoption, frequency, or share across the selected portfolio.'
  },
  established: {
    label: 'Established',
    portfolioLabel: 'Established Brands',
    sliderLabel: '% of revenue from Established Brands',
    share: 44,
    min: 5,
    max: 70,
    rationale: 'For Established Brands, the model illustrates how a small improvement can unlock meaningful value from a mature revenue base, even as resources shift toward newer products.'
  }
};
const DOWNSTREAM_PHRASES: Record<LifecyclePhase | 'portfolio', string[]> = {
  launch: ['Steeper growth', 'Higher peak', 'Stronger tail'],
  growth: ['More momentum', 'Higher peak', 'Stronger tail'],
  established: ['Greater share', 'Stronger retention', 'More value from the tail'],
  portfolio: ['More momentum', 'Higher peak', 'Stronger tail']
};
const FULL_PORTFOLIO = {
  sliderLabel: '% of group revenue in scope',
  min: 10,
  max: 100,
  rationale: 'Across the full portfolio, the model illustrates what a small improvement is worth on the entire group revenue base. Select a lifecycle phase to zoom into Launch, Growth or Established brands.'
};
export function SlideValueCalculator({}: SlideProps) {
  const [phase, setPhase] = useState<LifecyclePhase | null>(null);
  const [revenueB, setRevenueB] = useState(32);
  const [phasePercent, setPhasePercent] = useState(100);
  const [improvementPercent, setImprovementPercent] = useState(0.5);
  const [animationDone, setAnimationDone] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const phaseConfig = phase ? PHASES[phase] : FULL_PORTFOLIO;
  useEffect(() => {
    const timers = [setTimeout(() => setImprovementPercent(1), 1400), setTimeout(() => setAnimationDone(true), 2400)];
    return () => timers.forEach(clearTimeout);
  }, []);
  const upside = revenueB * 1000 * (phasePercent / 100) * (improvementPercent / 100);
  const upsideValue = useSpring(0, {
    bounce: 0,
    duration: 1600
  });
  const displayUpside = useTransform(upsideValue, latest => Math.round(latest).toLocaleString());
  useEffect(() => {
    if (revealed) upsideValue.set(upside);
  }, [revealed, upside, upsideValue]);
  const handleChange = (setter: (value: number) => void) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setter(Number(event.target.value));
  };
  const selectPhase = (nextPhase: LifecyclePhase) => {
    if (phase === nextPhase) {
      setPhase(null);
      setPhasePercent(100);
      return;
    }
    setPhase(nextPhase);
    setPhasePercent(PHASES[nextPhase].share);
  };
  const sliders = [{
    label: 'Group Revenue',
    value: `€${revenueB.toFixed(1)}bn`,
    valueClass: 'text-[var(--fg)]',
    min: 5,
    max: 65,
    step: 0.5,
    current: revenueB,
    onChange: handleChange(setRevenueB),
    footer: ['€5bn', 'Illustrative: €32bn', '€65bn']
  }, {
    label: phaseConfig.sliderLabel,
    value: `€${(revenueB * phasePercent / 100).toFixed(1)}bn`,
    valueClass: 'text-[var(--fg)]',
    min: phaseConfig.min,
    max: phaseConfig.max,
    step: 0.5,
    current: phasePercent,
    onChange: handleChange(setPhasePercent),
    footer: [`${phaseConfig.min}%`, `${phasePercent.toFixed(1)}% — illustrative`, `${phaseConfig.max}%`]
  }, {
    label: 'Target Improvement',
    value: `${improvementPercent.toFixed(1)}%`,
    valueClass: 'text-[var(--brand)]',
    min: 0.5,
    max: 5,
    step: 0.5,
    current: improvementPercent,
    onChange: handleChange(setImprovementPercent),
    footer: ['0.5%', '', '5%']
  }];
  return <InteriorSlide data-id="element-614" data-mp="cgym7hq9ny8e">
      <motion.header initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6
    }} className="mb-[clamp(1rem,1.8vw,1.6rem)] flex-shrink-0" data-id="element-615" data-mp="3b5nleq7g9crg">
        <div className="mb-3 flex items-center gap-3" data-id="element-616" data-mp="1x9cj4ubjdg2d">
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-617" data-mp="1x8tbc6flo822">
            Illustrative model
          </span>
          <span className="h-px w-12 bg-black/10" data-id="element-618" data-mp="1o8o4bxebjbof" />
          <span className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]" data-id="element-619" data-mp="1o73883ccvkog">
            Across the full lifecycle
          </span>
        </div>

        <motion.h2 initial={{
        opacity: 0,
        y: 5
      }} animate={{
        opacity: 1,
        y: 0
      }} className="max-w-5xl text-[clamp(1.6rem,3vw,2.75rem)] font-bold leading-[1.1] tracking-tight text-[var(--fg)]" data-id="element-620" data-mp="1eb9vojymzr6o">
          Even a{' '}
          <span className="text-[var(--brand)]" data-id="element-621" data-mp="15gc4bhb50mhg">
            {improvementPercent.toFixed(1)}%
          </span>{' '}
          improvement across the lifecycle equals
        </motion.h2>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-14" data-id="element-622" data-mp="vm3dqz5o33lx">
        <div className="relative space-y-5 rounded-3xl border border-black/5 bg-[var(--surface)] p-[clamp(1.25rem,2vw,2rem)] lg:col-span-5" data-id="element-623" data-mp="qnidizqf0jxh">
          <div data-id="element-624" data-mp="lwwnypx9b9ol">
            <div className="mb-2 flex items-center gap-2 text-[var(--brand)]" data-id="element-625" data-mp="lxfwxh4xinqh">
              <SlidersHorizontalIcon className="h-4 w-4" aria-hidden="true" data-id="element-626" data-mp="7xn7dp552dk3" />
              <p className="text-[10px] font-bold uppercase tracking-[0.15em]" data-id="element-627" data-mp="by74wttmjj6r">
                {phase ? `Zoomed into ${PHASES[phase].label} brands` : 'Full portfolio — select a phase to zoom in'}
              </p>
            </div>
            <div className="grid grid-cols-3 rounded-xl border border-[var(--brand)]/15 bg-white p-1 shadow-sm" role="group" aria-label="Select lifecycle phase" data-id="element-628" data-mp="14p8od74co0w3">
              {(Object.keys(PHASES) as LifecyclePhase[]).map(phaseKey => {
              const active = phase === phaseKey;
              return <button key={phaseKey} type="button" onClick={() => selectPhase(phaseKey)} aria-pressed={active} className={`rounded-lg px-2 py-2 text-xs font-bold transition ${active ? 'bg-[var(--brand)] text-white shadow-sm' : 'text-[var(--muted)] hover:bg-[var(--surface)] hover:text-[var(--fg)]'}`} data-id="element-629" data-mp="1nf6x7pfz1y0s">
                    {PHASES[phaseKey].label}
                  </button>;
            })}
            </div>
          </div>

          {sliders.map(slider => <div key={slider.label} className="space-y-2.5" data-id="element-630" data-mp="2z44dpodlp837">
              <div className="flex items-end justify-between gap-4" data-id="element-631" data-mp="2u4jinp1jxjvr">
                <label htmlFor={slider.label} className="text-sm font-semibold text-[var(--fg)]" data-id="element-632" data-mp="2u6clet91njyh">
                  {slider.label}
                </label>
                <span className={`text-2xl font-bold ${slider.valueClass}`} data-id="element-633" data-mp="9gdm1862ej7h">
                  {slider.value}
                </span>
              </div>
              <input id={slider.label} type="range" min={slider.min} max={slider.max} step={slider.step} value={slider.current} onChange={slider.onChange} className="deck-slider w-full" data-id="element-634" data-mp="27wmehrr0xfht" />
              <div className="flex justify-between text-xs text-[var(--muted)]" data-id="element-635" data-mp="2mmtnlgv55m5u">
                <span data-id="element-636" data-mp="2qkrdklwqkcaa">{slider.footer[0]}</span>
                <span className="text-[var(--brand)]" data-id="element-637" data-mp="2vloteru9wu8m">{slider.footer[1]}</span>
                <span data-id="element-638" data-mp="2vjm5qeglfd72">{slider.footer[2]}</span>
              </div>
            </div>)}
        </div>

        <div className="flex flex-col justify-center lg:col-span-7" data-id="element-639" data-mp="117e4ykhlc23d">
          {revealed ? <motion.div key={`revealed-${phase}`} initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.35,
          ease: [0.23, 1, 0.32, 1]
        }} className="space-y-4" data-id="element-640" data-mp="166hae61hgabv">
              <h3 className="text-base font-semibold uppercase tracking-[0.2em] text-[var(--muted)]" data-id="element-641" data-mp="tu6lq3ouya2p">
                Direct Value Unlocked
              </h3>
              <div className="flex items-baseline gap-2 text-[var(--brand)]" data-id="element-642" data-mp="jz4wz8yhva0n">
                <span className="text-[clamp(1.75rem,3vw,3rem)] font-bold" data-id="element-643" data-mp="1hmdi32ul2h24">€</span>
                <motion.span className="text-[clamp(3.5rem,8vw,7rem)] font-bold leading-none tracking-tight" data-id="element-644" data-mp="1cn0ntx1du7pi">
                  {displayUpside}
                </motion.span>
                <span className="text-[clamp(1.75rem,3vw,3rem)] font-bold" data-id="element-645" data-mp="17pqysihwny27">m</span>
              </div>
              <p className="max-w-xl border-l-2 border-[var(--brand)]/40 py-1 pl-4 text-[clamp(0.95rem,1.2vw,1.1rem)] leading-relaxed text-[var(--muted)]" data-id="element-646" data-mp="12qxmgtecud3m">
                {phaseConfig.rationale}
              </p>

              {/* Downstream effect — deliberately excluded from the figure above */}
              <div className="pt-[clamp(0.75rem,1.5vw,1.5rem)]" data-id="element-647" data-mp="2vxqkpa98rs7z">
                <motion.p initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 1.9,
              duration: 0.6
            }} className="text-[clamp(0.7rem,0.95vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--fg)]" data-id="element-648" data-mp="2r2l14i3bapn2">
                  And the effect may not stop there…
                </motion.p>

                <div className="relative mt-[clamp(1rem,2vw,1.75rem)] max-w-2xl" data-id="element-649" data-mp="3aofxui69jheh">
                  <svg viewBox="0 0 600 60" preserveAspectRatio="none" className="absolute inset-x-0 bottom-0 h-[clamp(2.5rem,5vh,3.75rem)] w-full" aria-hidden="true" data-id="element-650" data-mp="3onpbpb6zxzfa">
                    <motion.path d="M 10 52 C 170 50 210 30 300 24 C 400 17 470 10 590 6" fill="none" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round" initial={{
                  pathLength: 0,
                  opacity: 0
                }} animate={{
                  pathLength: 1,
                  opacity: 0.5
                }} transition={{
                  delay: 2.3,
                  duration: 1.6,
                  ease: 'easeInOut'
                }} data-id="element-651" data-mp="1pbwhfmblf869" />
                  </svg>

                  <div className="relative flex items-end justify-between gap-3" data-id="element-652" data-mp="3i7gymvpoiwy5">
                    {DOWNSTREAM_PHRASES[phase ?? 'portfolio'].map((phrase, index) => <motion.span key={phrase} initial={{
                  opacity: 0,
                  y: 12
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 2.6 + index * 0.55,
                  duration: 0.55
                }} style={{
                  marginBottom: index * 14
                }} className="text-[clamp(0.72rem,1.05vw,1rem)] font-extrabold uppercase tracking-[0.12em] text-[var(--brand)]" data-id="element-653" data-mp="38bdu5j7cum81" data-mpi={index}>
                          {phrase}
                        </motion.span>)}
                  </div>
                </div>

                <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 4.5,
              duration: 0.6
            }} className="mt-[clamp(1.25rem,2.5vw,2.25rem)] text-[clamp(0.62rem,0.8vw,0.75rem)] italic text-[var(--muted)]" data-id="element-654" data-mp="w3gn8poocpdk">
                  Potential downstream effects are not included in the
                  calculation.
                </motion.p>
              </div>
            </motion.div> : <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.35,
          ease: [0.23, 1, 0.32, 1]
        }} className="max-w-xl space-y-6" data-id="element-655" data-mp="26r5uxqzjw2xa">
              <h3 className="text-base font-semibold uppercase tracking-[0.2em] text-[var(--muted)]" data-id="element-656" data-mp="3ue0j3fq4o4bb">
                Unlocked Value
              </h3>
              <motion.p key={`rationale-${phase}`} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="text-[clamp(0.95rem,1.2vw,1.1rem)] leading-relaxed text-[var(--muted)]" data-id="element-657" data-mp="789hitt5yj9a">
                {phaseConfig.rationale}
              </motion.p>
              <motion.button type="button" onClick={() => setRevealed(true)} whileHover={{
            scale: 1.03
          }} whileTap={{
            scale: 0.98
          }} animate={{
            opacity: animationDone ? 1 : 0.4
          }} className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] px-8 py-4 text-lg font-bold text-white shadow-lg shadow-[var(--brand)]/25 transition-colors hover:bg-[var(--brand-light)]" data-id="element-658" data-mp="lwx96ozw28ty">
                <CalculatorIcon className="h-5 w-5" aria-hidden="true" data-id="element-659" data-mp="hbobhfrdkda9" />
                Calculate potential
              </motion.button>
            </motion.div>}
        </div>
      </div>
    </InteriorSlide>;
}